import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, CheckCircle, Clock, Settings, ArrowRight } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { activeTenant } = useTenant();
  const [stats, setStats] = useState({ pending: 0, inProgress: 0, blocked: 0 });
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [operators, setOperators] = useState([]);

  useEffect(() => {
    if (activeTenant) {
      // Load tasks stats
      api.get(`/tasks?tenant_id=${activeTenant}`).then(res => {
        const tasks = res.data;
        setStats({
          pending: tasks.filter(t => t.status === 'En proceso').length,
          inProgress: tasks.filter(t => t.status === 'Validando' || t.status === 'Exportado').length,
          blocked: tasks.filter(t => t.status === 'Pendiente cliente').length
        });
      }).catch(() => {});

      // Load onboarding status
      api.get(`/onboarding/${activeTenant}`).then(res => {
        setOnboardingStatus(res.data);
      }).catch(() => {});

      api.get(`/tenants/${activeTenant}/operators`).then((res) => {
        setOperators(res.data || []);
      }).catch(() => {});
    }
  }, [activeTenant]);

  const showOnboardingBanner = onboardingStatus && onboardingStatus.status !== 'completed';

  return (
    <div data-testid="client-dashboard" className="max-w-5xl mx-auto">
      <h1 className="text-4xl font-heading font-bold mb-2">Mi Panel</h1>
      <p className="text-muted-foreground mb-8">Bienvenido a tu espacio de trabajo</p>

      {/* Onboarding Banner */}
      {showOnboardingBanner && (
        <Card className="mb-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Settings className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Completa tu configuracion</h3>
                  <p className="text-sm text-muted-foreground">
                    {onboardingStatus.status === 'not_started'
                      ? 'Configura tu empresa para empezar a usar todos los servicios'
                      : `Progreso: ${onboardingStatus.progress_percentage}% completado`
                    }
                  </p>
                  {onboardingStatus.status !== 'not_started' && (
                    <Progress value={onboardingStatus.progress_percentage} className="w-48 h-2 mt-2" />
                  )}
                </div>
              </div>
              <Button onClick={() => navigate('/client/onboarding')} data-testid="continue-onboarding-btn">
                {onboardingStatus.status === 'not_started' ? 'Comenzar' : 'Continuar'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="hover:border-primary/50 hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <Clock className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent><div className="text-3xl font-heading font-bold">{stats.pending}</div></CardContent>
        </Card>

        <Card className="hover:border-primary/50 hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">En Progreso</CardTitle>
            <CheckCircle className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent><div className="text-3xl font-heading font-bold">{stats.inProgress}</div></CardContent>
        </Card>

        <Card className="hover:border-primary/50 hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Requieren Accion</CardTitle>
            <AlertCircle className="w-5 h-5 text-secondary" />
          </CardHeader>
          <CardContent><div className="text-3xl font-heading font-bold">{stats.blocked}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Acciones Rapidas</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="/client/requests" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">REQ</div>
              <div className="font-medium">Nueva Solicitud</div>
            </a>
            <a href="/client/tasks" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">TAR</div>
              <div className="font-medium">Mis Tareas</div>
            </a>
            <a href="/client/documents" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">DOC</div>
              <div className="font-medium">Documentos</div>
            </a>
            <a href="/client/billing" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">FAC</div>
              <div className="font-medium">Subscripcion</div>
            </a>
            <a href="/client/holded-exports" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">HEX</div>
              <div className="font-medium">Exportaciones Holded</div>
            </a>
            <a href="/client/users" className="p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
              <div className="text-xs mb-2">USR</div>
              <div className="font-medium">Colaboradores</div>
            </a>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Operador asignado</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {operators.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavia no hay operador asignado.</p>
          ) : (
            operators.map((op) => (
              <div key={op.id} className="flex items-center justify-between border rounded-lg px-4 py-3">
                <div>
                  <div className="font-medium">{op.full_name}</div>
                  <div className="text-xs text-muted-foreground">{op.email}</div>
                </div>
                <span className="text-xs text-muted-foreground">{op.role}</span>
              </div>
            ))
          )}
          <Button variant="outline" onClick={() => navigate('/client/chat')}>
            Ir al chat
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
export default ClientDashboard;
